import { defineBrowserModule, homeJsonLd } from "@amtana/browser";
import type { BrowserModule, SeoDeclaration } from "@amtana/browser";
import { PRODUCT_NAME, shippedLanguage } from "@amtana/i18n/catalog";
import type { Phrase } from "@amtana/i18n/catalog";

import { withBlogScreens } from "./blog.web.ts";
import { withComparisonScreens } from "./comparisons.web.ts";
import { LEGAL_ENTITY } from "./model/legal-entity.ts";
import { SITE_CONTENT } from "./model/site-content.ts";
import { SITE_URL } from "./model/site-url.ts";
import { withPricingScreens } from "./pricing.web.ts";
import { withUseCaseScreens } from "./use-cases.web.ts";

// The declaration: the only export of this package. ADR-027 point 2 (epic 31, S31.01 and
// S31.02): every screen but the catch-all carries `seo` next to its `title`, read the same way
// a title is, in the active language (`@amtana/architecture-enforcer`'s `screen-seo` policy
// refuses a website screen without one).

/** A tab title the site reads in the active language once a screen renders. */
function pageTitle(screen: keyof (typeof SITE_CONTENT)["en"]["PAGE_TITLES"]): Phrase {
  return {
    kind: "phrase",
    read: (language) => SITE_CONTENT[shippedLanguage(language)].PAGE_TITLES[screen],
  };
}

/** A page's own search description, read in the active language. */
function pageDescription(screen: keyof (typeof SITE_CONTENT)["en"]["SEO"]): Phrase {
  return {
    kind: "phrase",
    read: (language) => SITE_CONTENT[shippedLanguage(language)].SEO[screen].description,
  };
}

const homeKeyphrase: Phrase = {
  kind: "phrase",
  read: (language) => SITE_CONTENT[shippedLanguage(language)].SEO.home.keyphrase,
};

/** The home page's own structured data: the Organization behind the product (the Legal Notice's
 * own company, never the product's brand name), the WebSite and the SoftwareApplication, read in
 * the active language (ADR-027 point 2). */
function homeSeoJsonLd(language: string) {
  return homeJsonLd({
    siteUrl: SITE_URL,
    productName: PRODUCT_NAME,
    legalName: LEGAL_ENTITY.company,
    address: LEGAL_ENTITY.address,
    logo: "/brand/zaistev-icon-colour.png",
    description: SITE_CONTENT[shippedLanguage(language)].SEO.home.description,
  });
}

/** The files each page's words come from, relative to the repository (ADR-028 point 1): the
 * website build dates the page's sitemap `<lastmod>` by their last commit. */
const SITE = "modules/site/browser/src";
const SOURCES = {
  home: [
    `${SITE}/ui/blocks/landing-page.tsx`,
    `${SITE}/model/site-words-en.ts`,
    `${SITE}/model/site-words-es.ts`,
  ],
  brand: [`${SITE}/ui/sections/brand-screen.tsx`],
  terms: [
    `${SITE}/model/legal-terms-words-en.ts`,
    `${SITE}/model/legal-terms-words-es.ts`,
    `${SITE}/model/legal-entity.ts`,
  ],
  privacy: [
    `${SITE}/model/legal-privacy-words-en.ts`,
    `${SITE}/model/legal-privacy-words-es.ts`,
    `${SITE}/model/legal-entity.ts`,
  ],
  notice: [
    `${SITE}/model/legal-notice-words-en.ts`,
    `${SITE}/model/legal-notice-words-es.ts`,
    `${SITE}/model/legal-entity.ts`,
  ],
} as const satisfies Record<string, readonly string[]>;

const homeSeo: SeoDeclaration = {
  description: pageDescription("home"),
  keyphrase: homeKeyphrase,
  contentType: "landing",
  jsonLd: homeSeoJsonLd,
  sources: SOURCES.home,
};

const siteScreens = defineBrowserModule("site").withScreens({
  home: {
    path: "/",
    title: pageTitle("home"),
    seo: homeSeo,
    load: () => import("./ui/sections/home-screen.tsx"),
  },
  // The landing's second take (ui/sections/home-drift-screen.tsx): the same words, a different
  // backdrop, kept out of search so it never competes with the home page it is still being
  // measured against.
  homeDrift: {
    path: "/v2",
    title: pageTitle("home"),
    seo: {
      description: pageDescription("home"),
      keyphrase: homeKeyphrase,
      contentType: "landing",
      sitemap: false,
      noindex: true,
    },
    load: () => import("./ui/sections/home-drift-screen.tsx"),
  },
  brand: {
    path: "/brand",
    title: pageTitle("brand"),
    seo: { description: pageDescription("brand"), contentType: "landing", sources: SOURCES.brand },
    load: () => import("./ui/sections/brand-screen.tsx"),
  },
  terms: {
    path: "/legal/terms",
    title: pageTitle("terms"),
    seo: { description: pageDescription("terms"), contentType: "legal", sources: SOURCES.terms },
    load: () => import("./ui/sections/legal-terms-screen.tsx"),
  },
  privacy: {
    path: "/legal/privacy",
    title: pageTitle("privacy"),
    seo: { description: pageDescription("privacy"), contentType: "legal", sources: SOURCES.privacy },
    load: () => import("./ui/sections/legal-privacy-screen.tsx"),
  },
  notice: {
    path: "/legal/notice",
    title: pageTitle("notice"),
    seo: { description: pageDescription("notice"), contentType: "legal", sources: SOURCES.notice },
    load: () => import("./ui/sections/legal-notice-screen.tsx"),
  },
  notFound: {
    path: "*",
    title: pageTitle("notFound"),
    load: () => import("./ui/sections/not-found-screen.tsx"),
  },
});

// ADR-034 point 1: each page family declares its own screens in its own `*.web.ts`.
export const siteBrowserModule: BrowserModule = withBlogScreens(
  withComparisonScreens(withUseCaseScreens(withPricingScreens(siteScreens))),
);
